import React from "react";
import { Box, Typography, Paper, Chip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { tokens } from "../../../theme";
import { RootState } from "../../../store/Index";
import BookIcon from "@mui/icons-material/Book";
import FavoriteIcon from "@mui/icons-material/Favorite";

const MementoSection: React.FC = () => {
  const theme = useTheme();
  const themeState = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, themeState);

  return (
    <Box
      id="memento"
      component="section"
      sx={{
        py: { xs: 12, md: 15 },
        bgcolor: colors.primary[400],
        color: colors.primary[900],
        overflow: "hidden",
        position: "relative",
      }}
    >
      <Box
        sx={{
          maxWidth: "1280px",
          mx: "auto",
          px: { xs: 2, sm: 3, lg: 4 },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          gap: { xs: 8, md: 16 },
        }}
      >
        {/* Left Side - Content */}
        <Box sx={{ flex: 1 }}>
          {/* Badge */}
          <Chip
            icon={<BookIcon sx={{ fontSize: 16 }} />}
            label="PROPRIETARY TECH"
            sx={{
              mb: 4,
              bgcolor: `${colors.primary[700]}20`,
              border: `1px solid ${colors.primary[700]}30`,
              color: `${colors.primary[900]}70`,
              fontWeight: 700,
              fontSize: "0.75rem",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              "& .MuiChip-icon": {
                color: `${colors.primary[900]}70`,
              },
            }}
          />

          {/* Heading */}
          <Typography
            variant="h2"
            sx={{
              fontFamily: theme.typography.fontFamily,
              fontSize: { xs: "2rem", md: "3rem" },
              fontWeight: 700,
              color: colors.primary[900],
              mb: 3,
            }}
          >
            Muzukuru Memento
          </Typography>

          {/* Description */}
          <Typography
            sx={{
              fontFamily: theme.typography.fontFamily,
              fontSize: { xs: "1rem", md: "1.25rem" },
              color: `${colors.primary[900]}70`,
              mb: 5,
              lineHeight: 1.6,
            }}
          >
            We turn ephemeral digital moments into lasting physical legacies.
            Our proprietary <strong>Memento App</strong> automatically compiles
            digital tributes, photos, and live comments into a
            professional-grade physical keepsake book.
          </Typography>

          {/* Stats Grid */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 4,
            }}
          >
            <Box
              sx={{
                borderLeft: `2px solid ${colors.primary[200]}`,
                pl: 3,
              }}
            >
              <Typography
                sx={{
                  fontSize: "1.875rem",
                  fontWeight: 700,
                  mb: 0.5,
                  textDecoration: "underline",
                  textDecorationColor: colors.primary[200],
                  textDecorationThickness: "2px",
                  textUnderlineOffset: "4px",
                  letterSpacing: "-0.05em",
                  textTransform: "uppercase",
                  color: colors.primary[900],
                }}
              >
                Automated
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  color: `${colors.primary[900]}60`,
                  fontWeight: 600,
                }}
              >
                Layout & Processing
              </Typography>
            </Box>

            <Box
              sx={{
                borderLeft: `2px solid ${colors.primary[200]}`,
                pl: 3,
              }}
            >
              <Typography
                sx={{
                  fontSize: "1.875rem",
                  fontWeight: 700,
                  mb: 0.5,
                  textDecoration: "underline",
                  textDecorationColor: colors.primary[200],
                  textDecorationThickness: "2px",
                  textUnderlineOffset: "4px",
                  letterSpacing: "-0.05em",
                  textTransform: "uppercase",
                  color: colors.primary[900],
                }}
              >
                Premium
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  color: `${colors.primary[900]}60`,
                  fontWeight: 600,
                }}
              >
                Hardcover Printing
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Right Side - Book Mockup */}
        <Box
          sx={{
            flex: 1,
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "120%",
              height: "120%",
              bgcolor: `${colors.primary[400]}20`,
              filter: "blur(100px)",
              borderRadius: "50%",
              zIndex: 0,
            }}
          />

          {/* Book Card */}
          <Paper
            elevation={24}
            sx={{
              position: "relative",
              zIndex: 1,
              width: "100%",
              maxWidth: "400px",
              aspectRatio: "3/4",
              bgcolor: colors.primary[900],
              borderRadius: 2,
              p: 4,
              transform: "rotate(3deg)",
              transition: "transform 0.5s ease",
              "&:hover": {
                transform: "rotate(0deg)",
              },
            }}
          >
            <Box
              sx={{
                height: "100%",
                border: `1px solid ${colors.primary[800]}`,
                p: 3,
                display: "flex",
                flexDirection: "column",
                color: colors.primary[700],
              }}
            >
              {/* Book Header */}
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Box
                  sx={{
                    width: 64,
                    height: 4,
                    bgcolor: colors.primary[800],
                    mx: "auto",
                    mb: 3,
                  }}
                />
                <FavoriteIcon
                  sx={{
                    fontSize: 32,
                    color: colors.primary[100],
                    mb: 2,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "1.5rem",
                    fontFamily: "serif",
                    fontWeight: 700,
                    color: colors.primary[100],
                  }}
                >
                  In Loving Memory
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.625rem",
                    letterSpacing: "0.2em",
                    textTransform: "uppercase",
                    color: colors.primary[400],
                    mt: 1,
                  }}
                >
                  The Collection of Tributes
                </Typography>
              </Box>

              {/* Comments/Tributes */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                {/* Tribute 1 */}
                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      bgcolor: colors.primary[800],
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        height: 8,
                        bgcolor: colors.primary[800],
                        borderRadius: "50px",
                        width: "33%",
                        mb: 1,
                      }}
                    />
                    <Box
                      sx={{
                        height: 40,
                        bgcolor: colors.primary[800],
                        borderRadius: 1,
                        fontStyle: "italic",
                        fontSize: "0.625rem",
                        p: 1,
                        color: colors.primary[400],
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {"\"Your light will always shine in our hearts...\""}
                    </Box>
                  </Box>
                </Box>

                {/* Tribute 2 */}
                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      bgcolor: colors.primary[800],
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        height: 8,
                        bgcolor: colors.primary[800],
                        borderRadius: "50px",
                        width: "25%",
                        mb: 1,
                      }}
                    />
                    <Box
                      sx={{
                        height: 40,
                        bgcolor: colors.primary[800],
                        borderRadius: 1,
                        fontStyle: "italic",
                        fontSize: "0.625rem",
                        p: 1,
                        color: colors.primary[400],
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {"\"A true friend and mentor. Rest in peace.\""}
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Footer */}
              <Typography
                sx={{
                  textAlign: "center",
                  mt: "auto",
                  opacity: 0.3,
                  fontStyle: "italic",
                  fontSize: "0.5rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.15em",
                  fontWeight: 700,
                  color: colors.primary[700],
                }}
              >
                A Muzukuru Memento Production
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default MementoSection;